/**
 * Модель виджета «План и ход агроцикла»: этапы плана и работы — одной
 * лентой времени. Ничего не считает заново: окна — план сервера
 * (`/agrocycles/{key}`), ход работы — итоги кампании (`/season/picture`).
 * Здесь только сведение двух ответов по `campaign_id` и раскладка по шкале.
 */
import type { Work } from '../../data/types';
import type { PlanStage, PlanWork, StageProgress } from '../agrocycle/agrocycleApi';

/** Состояние карточки работы на дату среза. */
export type ItemState = 'done' | 'active' | 'soon' | 'ahead' | 'cancelled';
/** Состояние этапа: закрыт, идёт (срез внутри окна), впереди. */
export type StageState = 'done' | 'active' | 'ahead';

export interface PlanItem {
  key: string;
  name: string;
  from: string | null;
  to: string | null;
  approximate: boolean;
  state: ItemState;
  /** Кампания с итогами; `null` — работа плана ещё не запущена. */
  work: Work | null;
  plan: PlanWork | null;
  /** Доля выполненного: по га работ, без площади — по числу работ на полях. */
  done: number | null;
}

export interface StageCard {
  key: string;
  name: string;
  order: number;
  from: string | null;
  to: string | null;
  state: StageState;
  items: PlanItem[];
  /** Доля выполненного по запущенным работам этапа (та же ось, что у работ). */
  done: number | null;
  plan: PlanStage | null;
  /** Полоса на шкале: `null` — у этапа нет дат. */
  lane: number;
}

const ratio = (part: number, whole: number): number | null => (whole > 0 ? part / whole : null);

export function workDone(work: Work): number | null {
  const area = work.totals.area;
  // «Выполнено» — без «в процессе»: та же ось, что у KPI цикла и `stage_progress`
  // сервера (ИИ читает её же). `reported` у площади — выполнено + в процессе.
  if (area && area.total > 0) return ratio(area.reported - (area.inProgress ?? 0), area.total);
  return ratio(work.totals.reported, work.totals.obligations);
}

function workState(work: Work): ItemState {
  return work.status === 'done' ? 'done' : work.status === 'active' ? 'active' : 'ahead';
}

function byDates(from: string | null, to: string | null, asOf: string | null): StageState {
  if (!asOf || !from) return 'ahead';
  if (to && to < asOf) return 'done';
  if (from <= asOf) return 'active';
  return 'ahead';
}

function minOf(values: (string | null)[]): string | null {
  const known = values.filter((v): v is string => !!v).sort();
  return known[0] ?? null;
}
function maxOf(values: (string | null)[]): string | null {
  const known = values.filter((v): v is string => !!v).sort();
  return known[known.length - 1] ?? null;
}

function stageDone(items: PlanItem[]): number | null {
  let area = 0;
  let areaDone = 0;
  let count = 0;
  let countDone = 0;
  let allArea = true;
  for (const item of items) {
    const w = item.work;
    if (!w || w.status === 'upcoming') continue;
    if (w.totals.area && w.totals.area.total > 0) {
      area += w.totals.area.total;
      areaDone += w.totals.area.reported - (w.totals.area.inProgress ?? 0);
    } else allArea = false;
    count += w.totals.obligations;
    countDone += w.totals.reported;
  }
  return allArea && area > 0 ? ratio(areaDone, area) : ratio(countDone, count);
}

/**
 * Полосы шкалы: этапы, чьи полосы пересекаются на экране, уходят на соседнюю.
 * `span` — положение полосы 0..1 с учётом её наименьшей ширины (короткий
 * этап рисуется шире своего окна, и сосед по датам иначе лёг бы поверх).
 */
export function assignLanes<T extends { from: string | null; lane: number }>(
  stages: T[],
  span: (stage: T) => { left: number; right: number } | null,
): number {
  const ends: number[] = [];
  const placed = stages.map((stage) => ({ stage, pos: stage.from ? span(stage) : null }));
  for (const { stage, pos } of placed.sort((a, b) => (a.pos?.left ?? 0) - (b.pos?.left ?? 0))) {
    if (!pos) {
      stage.lane = -1;
      continue;
    }
    let lane = ends.findIndex((e) => e <= pos.left);
    if (lane < 0) {
      lane = ends.length;
      ends.push(pos.right);
    } else ends[lane] = pos.right;
    stage.lane = lane;
  }
  return ends.length;
}

/**
 * Этапы плана сервера с работами; запущенная работа берёт ход своей кампании.
 * Без плана сервера (фикстуры) этапы — группы работ по стадии.
 */
export function buildStages(
  planStages: ReadonlyArray<PlanStage> | null | undefined,
  works: Work[],
  asOf: string | null,
  progress?: ReadonlyArray<StageProgress> | null,
): StageCard[] {
  // Ход этапа — с сервера, если он его посчитал: одно число на экране и у ИИ.
  const serverDone = new Map((progress ?? []).filter((r) => r.done_pct !== null).map((r) => [r.code, r.done_pct! / 100]));
  const byCampaign = new Map(works.map((w) => [w.id, w]));
  const stages: StageCard[] = [];
  if (planStages && planStages.length > 0) {
    for (const stage of planStages) {
      const items: PlanItem[] = stage.works.map((pw) => {
        const work = pw.campaign_id != null ? byCampaign.get(String(pw.campaign_id)) ?? null : null;
        const state: ItemState =
          pw.state === 'cancelled'
            ? 'cancelled'
            : work
              ? workState(work)
              : pw.upcoming === 'soon'
                ? 'soon'
                : pw.state === 'launched'
                  ? byDates(pw.window_from, pw.window_to, asOf) === 'done' ? 'done' : byDates(pw.window_from, pw.window_to, asOf) === 'active' ? 'active' : 'ahead'
                  : 'ahead';
        return {
          key: `p${pw.id}`,
          name: pw.label,
          from: pw.window_from ?? work?.window.from ?? null,
          to: pw.window_to ?? work?.window.to ?? null,
          approximate: pw.approximate,
          state,
          work,
          plan: pw,
          done: work ? workDone(work) : null,
        };
      });
      const from = stage.window_from ?? minOf(items.map((i) => i.from));
      const to = stage.window_to ?? maxOf(items.map((i) => i.to));
      stages.push({
        key: `s${stage.id}`,
        name: stage.name,
        order: stage.order_no,
        from,
        to,
        state: byDates(from, to, asOf),
        items,
        done: serverDone.get(stage.code) ?? stageDone(items),
        plan: stage,
        lane: 0,
      });
    }
  } else {
    const groups = new Map<string, Work[]>();
    for (const work of [...works].sort((a, b) => a.seq - b.seq)) {
      const list = groups.get(work.stage) ?? [];
      list.push(work);
      groups.set(work.stage, list);
    }
    let order = 0;
    for (const [name, list] of groups) {
      order += 1;
      const items: PlanItem[] = list.map((work) => ({
        key: `w${work.id}`,
        name: work.name,
        from: work.window.from,
        to: work.window.to,
        approximate: false,
        state: workState(work),
        work,
        plan: null,
        done: workDone(work),
      }));
      const from = minOf(items.map((i) => i.from));
      const to = maxOf(items.map((i) => i.to));
      stages.push({ key: `g${order}`, name, order, from, to, state: byDates(from, to, asOf), items, done: stageDone(items), plan: null, lane: 0 });
    }
  }
  return stages;
}

/** Этап по умолчанию: идущий (последний из идущих), иначе ближайший впереди, иначе последний. */
export function focusStage(stages: StageCard[]): string | null {
  const active = stages.filter((s) => s.state === 'active');
  if (active.length) return active[active.length - 1]!.key;
  const ahead = stages.find((s) => s.state === 'ahead' && s.from);
  return ahead?.key ?? stages[stages.length - 1]?.key ?? null;
}

const DAY = 86_400_000;
const toTime = (iso: string) => Date.parse(`${iso}T00:00:00Z`);

export interface Axis {
  /** Положение даты на шкале, 0..1; `null` — вне шкалы. */
  at: (iso: string | null) => number | null;
  months: { iso: string; pos: number }[];
}

/** Шкала от начала месяца первой даты до конца месяца последней. */
export function makeAxis(dates: (string | null)[]): Axis | null {
  const lo = minOf(dates);
  const hi = maxOf(dates);
  if (!lo || !hi) return null;
  const start = Date.UTC(Number(lo.slice(0, 4)), Number(lo.slice(5, 7)) - 1, 1);
  const endMonth = new Date(Date.UTC(Number(hi.slice(0, 4)), Number(hi.slice(5, 7)), 1));
  const end = endMonth.getTime();
  const span = Math.max(DAY, end - start);
  const at = (iso: string | null) => {
    if (!iso) return null;
    const v = (toTime(iso) - start) / span;
    return v < 0 || v > 1 ? null : v;
  };
  const months: Axis['months'] = [];
  for (let d = new Date(start); d.getTime() < end; d = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 1, 1))) {
    const iso = d.toISOString().slice(0, 10);
    months.push({ iso, pos: (d.getTime() - start) / span });
  }
  return { at, months };
}
